import React from 'react';
import { Church, Heart, Users, Award, Clock, BookOpen } from 'lucide-react';
import { DonutChart } from './DonutChart';
import { ProgressBar } from './ProgressBar';
import { StatCard } from './StatCard';
import { StatSection } from './StatSection';

interface SpiritualStatsProps {
  spiritualStats: {
    totalConDatosComplementarios: number;
    bautizados: number;
    noBautizados: number;
    oracionFe: number;
    sinOracionFe: number;
    perteneceMinisterio: number;
    noPerteneceMinisterio: number;
    topMinisterios: Array<{ ministerio: string; count: number }>;
    topHabilidades: Array<{ habilidad: string; count: number }>;
    topDones: Array<{ don: string; count: number }>;
    distribucionTiempoAsistencia: Array<{ tiempo: string; count: number }>;
  };
  totalMembers: number;
}

export const SpiritualStats: React.FC<SpiritualStatsProps> = ({
  spiritualStats,
  totalMembers
}) => {
  const {
    totalConDatosComplementarios,
    bautizados,
    noBautizados,
    oracionFe,
    sinOracionFe,
    perteneceMinisterio,
    noPerteneceMinisterio,
    topMinisterios,
    topHabilidades,
    topDones,
    distribucionTiempoAsistencia
  } = spiritualStats;

  // Colores para gráficos
  const bautizadoColor = '#10b981';
  const noBautizadoColor = '#ef4444';
  const oracionColor = '#3b82f6';
  const sinOracionColor = '#94a3b8';
  const ministerioColor = '#8b5cf6';
  const noMinisterioColor = '#cbd5e1';

  return (
    <div className="space-y-6">
      {/* Cards de resumen espiritual */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          title="Con Datos Complementarios"
          value={totalConDatosComplementarios}
          icon={<Users className="w-6 h-6" />}
          color="indigo"
          subtitle={`${((totalConDatosComplementarios / Math.max(totalMembers, 1)) * 100).toFixed(1)}% de miembros`}
        />
        <StatCard
          title="Bautizados"
          value={bautizados}
          icon={<Church className="w-6 h-6" />}
          color="green"
          subtitle={`${((bautizados / Math.max(totalConDatosComplementarios, 1)) * 100).toFixed(1)}% con datos`}
        />
        <StatCard
          title="Oración de Fe"
          value={oracionFe}
          icon={<Heart className="w-6 h-6" />}
          color="blue"
          subtitle={`${((oracionFe / Math.max(totalConDatosComplementarios, 1)) * 100).toFixed(1)}% con datos`}
        />
        <StatCard
          title="En Ministerio"
          value={perteneceMinisterio}
          icon={<Award className="w-6 h-6" />}
          color="purple"
          subtitle={`${((perteneceMinisterio / Math.max(totalConDatosComplementarios, 1)) * 100).toFixed(1)}% con datos`}
        />
      </div>

      {/* Gráficos de distribución espiritual */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Bautizados */}
        <StatSection title="Estado de Bautismo" icon={Church} iconColor="text-green-600">
          <div className="flex flex-col items-center">
            <DonutChart
              data={[
                { label: 'Bautizados', value: bautizados, color: bautizadoColor },
                { label: 'No bautizados', value: noBautizados, color: noBautizadoColor }
              ]}
              size={120}
              centerText={`${((bautizados / Math.max(totalConDatosComplementarios, 1)) * 100).toFixed(0)}%`}
              centerSubtext="Bautizados"
            />
            <div className="flex justify-center space-x-4 mt-4 text-xs">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: bautizadoColor }}></div>
                <span className="text-gray-600">Sí: {bautizados}</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: noBautizadoColor }}></div>
                <span className="text-gray-600">No: {noBautizados}</span>
              </div>
            </div>
          </div>
        </StatSection>

        {/* Oración de fe */}
        <StatSection title="Oración de Fe" icon={Heart} iconColor="text-blue-600">
          <div className="flex flex-col items-center">
            <DonutChart
              data={[
                { label: 'Sí', value: oracionFe, color: oracionColor },
                { label: 'No', value: sinOracionFe, color: sinOracionColor }
              ]}
              size={120}
              centerText={`${((oracionFe / Math.max(totalConDatosComplementarios, 1)) * 100).toFixed(0)}%`}
              centerSubtext="Han orado"
            />
            <div className="flex justify-center space-x-4 mt-4 text-xs">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: oracionColor }}></div>
                <span className="text-gray-600">Sí: {oracionFe}</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: sinOracionColor }}></div>
                <span className="text-gray-600">No: {sinOracionFe}</span>
              </div>
            </div>
          </div>
        </StatSection>

        {/* Pertenencia a ministerio */}
        <StatSection title="Pertenencia a Ministerio" icon={Award} iconColor="text-purple-600">
          <div className="flex flex-col items-center">
            <DonutChart
              data={[
                { label: 'Sí', value: perteneceMinisterio, color: ministerioColor },
                { label: 'No', value: noPerteneceMinisterio, color: noMinisterioColor }
              ]}
              size={120}
              centerText={`${((perteneceMinisterio / Math.max(totalConDatosComplementarios, 1)) * 100).toFixed(0)}%`}
              centerSubtext="En ministerio"
            />
            <div className="flex justify-center space-x-4 mt-4 text-xs">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: ministerioColor }}></div>
                <span className="text-gray-600">Sí: {perteneceMinisterio}</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: noMinisterioColor }}></div>
                <span className="text-gray-600">No: {noPerteneceMinisterio}</span>
              </div>
            </div>
          </div>
        </StatSection>
      </div>

      {/* Top listados */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Ministerios */}
        {topMinisterios.length > 0 && (
          <StatSection title="Top Ministerios" icon={Award} iconColor="text-orange-600">
            <div className="space-y-3">
              {topMinisterios.map((min, index) => (
                <ProgressBar
                  key={index}
                  label={min.ministerio}
                  value={min.count}
                  total={perteneceMinisterio || 1}
                  color={index === 0 ? '#f97316' : index === 1 ? '#3b82f6' : '#8b5cf6'}
                />
              ))}
            </div>
          </StatSection>
        )}

        {/* Top Habilidades */}
        {topHabilidades.length > 0 && (
          <StatSection title="Top Habilidades" icon={Users} iconColor="text-teal-600">
            <div className="space-y-3">
              {topHabilidades.map((hab, index) => (
                <ProgressBar
                  key={index}
                  label={hab.habilidad}
                  value={hab.count}
                  total={totalConDatosComplementarios || 1}
                  color={index === 0 ? '#14b8a6' : index === 1 ? '#f59e0b' : '#ec4899'}
                />
              ))}
            </div>
          </StatSection>
        )}

        {/* Top Dones */}
        {topDones.length > 0 && (
          <StatSection title="Top Dones Espirituales" icon={Award} iconColor="text-red-600">
            <div className="space-y-3">
              {topDones.map((don, index) => (
                <ProgressBar
                  key={index}
                  label={don.don}
                  value={don.count}
                  total={totalConDatosComplementarios || 1}
                  color={index === 0 ? '#ef4444' : index === 1 ? '#8b5cf6' : '#3b82f6'}
                />
              ))}
            </div>
          </StatSection>
        )}
      </div>

      {/* Distribución por tiempo de asistencia */}
      {distribucionTiempoAsistencia.length > 0 && (
        <StatSection title="Tiempo de Asistencia a la Iglesia" icon={Clock} iconColor="text-indigo-600">
          <div className="space-y-3">
            {distribucionTiempoAsistencia.map((tiempo, index) => (
              <ProgressBar
                key={index}
                label={tiempo.tiempo}
                value={tiempo.count}
                total={totalConDatosComplementarios || 1}
                color={index === 0 ? '#6366f1' : index === 1 ? '#8b5cf6' : index === 2 ? '#a78bfa' : '#c4b5fd'}
              />
            ))}
          </div>
        </StatSection>
      )}
    </div>
  );
};

export default SpiritualStats;
